'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import ProjectsTab from './ProjectsTab';
import ActivitiesTab from './ActivitiesTab';
import HabitsTab from './HabitsTab';
import MELAutoSummary from './MELAutoSummary';
import SubmitBar from './SubmitBar';
import SubmissionConfirmation from './SubmissionConfirmation';

type TabKey = 'projects' | 'activities' | 'habits';

const tabs: { key: TabKey; label: string; icon: string; count: number }[] = [
  { key: 'projects', label: 'Projects', icon: 'FolderOpenIcon', count: 4 },
  { key: 'activities', label: 'Activities', icon: 'ClipboardDocumentListIcon', count: 6 },
  { key: 'habits', label: 'Habits', icon: 'HeartIcon', count: 9 },
];

// Shared form data store — tabs write here via onChange callbacks
let _formSnapshot: Record<string, string> = {};

export function updateFormSnapshot(key: string, value: string) {
  _formSnapshot[key] = value;
}

export default function ActivityLogEntryScreen() {
  const [activeTab, setActiveTab] = useState<TabKey>('projects');
  const [month, setMonth] = useState('April 2026');
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'submitted'>('idle');
  const [habitScore, setHabitScore] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submissionSummary, setSubmissionSummary] = useState<{
    month: string;
    centre: string;
    submittedAt: string;
    referenceId: string;
    projects: {
      kalpTaruhTrees: string;
      pvParticipants: string;
      pvChampion1Name: string;
      pvChampion2Name: string;
      pvChampion3Name: string;
      gokulVillageName: string;
    };
    activities: {
      sakashAmritvela: string;
      sakashNumaShaam: string;
      awarenessJeevan: string;
      awarenessVidyarthi: string;
      walksCount: string;
      cleanupCount: string;
      competitionType: string;
      workshopCount: string;
    };
    habitScore: number;
  } | null>(null);

  const months = ['May 2026', 'April 2026', 'March 2026', 'February 2026'];

  const handleChange = () => setHasUnsaved(true);
  const handleHabitScoreChange = (score: number) => setHabitScore(score);

  const generateRefId = () => {
    const prefix = 'PV';
    const ts = Date.now().toString(36).toUpperCase();
    return `${prefix}-${ts}`;
  };

  const handleSubmit = async () => {
    setSubmitStatus('loading');
    // Backend integration point: POST /api/activity-logs with full form payload
    await new Promise((r) => setTimeout(r, 1800));
    setSubmitStatus('submitted');
    setHasUnsaved(false);

    // Build summary from snapshot
    const snap = _formSnapshot;
    const now = new Date();
    const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    const dateStr = now.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

    setSubmissionSummary({
      month,
      centre: 'Pune Shivajinagar BK Centre',
      submittedAt: `${dateStr}, ${timeStr}`,
      referenceId: generateRefId(),
      projects: {
        kalpTaruhTrees: snap['kalpTaruhTrees'] ?? '',
        pvParticipants: snap['pvParticipants'] ?? '',
        pvChampion1Name: snap['pvChampion1Name'] ?? '',
        pvChampion2Name: snap['pvChampion2Name'] ?? '',
        pvChampion3Name: snap['pvChampion3Name'] ?? '',
        gokulVillageName: snap['gokulVillageName'] ?? '',
      },
      activities: {
        sakashAmritvela: snap['sakashAmritvela'] ?? '',
        sakashNumaShaam: snap['sakashNumaShaam'] ?? '',
        awarenessJeevan: snap['awarenessJeevan'] ?? '0',
        awarenessVidyarthi: snap['awarenessVidyarthi'] ?? '0',
        walksCount: snap['walksCount'] ?? '0',
        cleanupCount: snap['cleanupCount'] ?? '0',
        competitionType: snap['competitionType'] ?? '',
        workshopCount: snap['workshopCount'] ?? '0',
      },
      habitScore,
    });

    setShowConfirmation(true);
  };

  const handleLogAnother = () => {
    setShowConfirmation(false);
    setSubmitStatus('idle');
    setHasUnsaved(false);
    setHabitScore(0);
    _formSnapshot = {};
    setActiveTab('projects');
  };

  // Show confirmation screen after submission
  if (showConfirmation && submissionSummary) {
    return <SubmissionConfirmation summary={submissionSummary} onLogAnother={handleLogAnother} />;
  }

  return (
    <div className="space-y-5 pb-28">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground">Log Monthly Activities</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Pune Shivajinagar BK Centre — submit your environmental activity report</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="relative">
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 rounded-xl border border-border bg-card text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer"
              aria-label="Select reporting month"
            >
              {months.map((m) => (
                <option key={`month-sel-${m}`} value={m}>{m}</option>
              ))}
            </select>
            <Icon name="ChevronDownIcon" size={14} variant="outline" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
          {hasUnsaved && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--warning-bg)] text-[var(--warning)] text-xs font-semibold animate-fade-in">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--warning)]" />
              Unsaved changes
            </span>
          )}
          {submitStatus === 'submitted' && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--success-bg)] text-[var(--success)] text-xs font-semibold animate-fade-in">
              <Icon name="CheckCircleIcon" size={14} variant="outline" />
              Submitted
            </span>
          )}
        </div>
      </div>

      {/* Progress overview */}
      <div className="grid grid-cols-3 gap-3">
        {tabs.map((tab) => (
          <div
            key={`progress-${tab.key}`}
            className={`rounded-xl border p-3 cursor-pointer transition-all duration-150 ${activeTab === tab.key ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/40'}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <div className="flex items-center gap-2 mb-2">
              <Icon name={tab.icon as any} size={16} variant="outline" className={activeTab === tab.key ? 'text-primary' : 'text-muted-foreground'} />
              <span className={`text-xs font-semibold ${activeTab === tab.key ? 'text-primary' : 'text-muted-foreground'}`}>{tab.label}</span>
            </div>
            <p className={`text-lg font-extrabold tabular-nums ${activeTab === tab.key ? 'text-primary' : 'text-foreground'}`}>{tab.count}</p>
            <p className="text-[10px] text-muted-foreground">items to report</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-muted p-1 rounded-xl">
        {tabs.map((tab) => (
          <button
            key={`tab-btn-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-150 ${activeTab === tab.key ? 'tab-active' : 'tab-inactive'}`}
          >
            <Icon name={tab.icon as any} size={15} variant="outline" />
            <span className="hidden sm:inline">{tab.label}</span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? 'bg-white/20 text-white' : 'bg-muted-foreground/20 text-muted-foreground'}`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="animate-fade-in">
        {activeTab === 'projects' && <ProjectsTab onChange={handleChange} onSnapshot={(k, v) => { _formSnapshot[k] = v; }} />}
        {activeTab === 'activities' && <ActivitiesTab onChange={handleChange} onSnapshot={(k, v) => { _formSnapshot[k] = v; }} />}
        {activeTab === 'habits' && <HabitsTab onChange={handleChange} onScoreChange={handleHabitScoreChange} />}
      </div>

      {/* MEL Summary */}
      <MELAutoSummary />

      {/* Sticky submit bar */}
      <SubmitBar
        month={month}
        status={submitStatus}
        hasUnsaved={hasUnsaved}
        onSubmit={handleSubmit}
        onSaveDraft={() => { setHasUnsaved(false); }}
      />
    </div>
  );
}